"use client";

import { useState, useEffect, useCallback } from "react";
import { X, Download, Share } from "lucide-react";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISS_KEY = "pwa-install-dismissed";
const DELAY_MS = 3 * 60 * 1000; // 3 minutes

function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !("MSStream" in window);
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    ("standalone" in navigator && (navigator as unknown as { standalone: boolean }).standalone)
  );
}

export function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);

  const dismiss = useCallback(() => {
    setShowPrompt(false);
    setShowIOSPrompt(false);
    try {
      localStorage.setItem(DISMISS_KEY, Date.now().toString());
    } catch {
      // localStorage unavailable
    }
  }, []);

  useEffect(() => {
    // Don't show if already installed or recently dismissed
    if (isStandalone()) return;

    try {
      const dismissed = localStorage.getItem(DISMISS_KEY);
      if (dismissed) {
        const elapsed = Date.now() - parseInt(dismissed, 10);
        // Don't show again for 7 days
        if (elapsed < 7 * 24 * 60 * 60 * 1000) return;
      }
    } catch {
      // localStorage unavailable
    }

    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);

    // Show prompt after delay
    const timer = setTimeout(() => {
      if (isIOS()) {
        setShowIOSPrompt(true);
      } else {
        // On Android/desktop the beforeinstallprompt may have already fired
        setShowPrompt(true);
      }
    }, DELAY_MS);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      clearTimeout(timer);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setShowPrompt(false);
    }
    setDeferredPrompt(null);
  };

  // iOS instructions banner
  if (showIOSPrompt) {
    return (
      <div className="fixed bottom-0 inset-x-0 z-50 animate-slide-up">
        <div className="mx-4 mb-4 rounded-2xl bg-white border border-emerald-200 shadow-2xl p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
              <Share className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-slate-900 text-sm">
                Add SekaniPay to Home Screen
              </h3>
              <p className="text-xs text-slate-500 mt-1">
                Tap <Share className="inline w-3.5 h-3.5 -mt-0.5" /> then &quot;Add to Home Screen&quot; for quick access.
              </p>
            </div>
            <button
              onClick={dismiss}
              className="flex-shrink-0 p-1 rounded-lg hover:bg-slate-100 transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Android / Desktop install banner
  if (!showPrompt || !deferredPrompt) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-50 animate-slide-up">
      <div className="mx-4 mb-4 rounded-2xl bg-white border border-emerald-200 shadow-2xl p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
            <Download className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-900 text-sm">
              Install SekaniPay
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Add to your home screen for quick access to the payroll calculator.
            </p>
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleInstall}
                className="px-4 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700 transition-colors"
              >
                Install
              </button>
              <button
                onClick={dismiss}
                className="px-4 py-1.5 text-slate-500 text-xs font-medium rounded-lg hover:bg-slate-100 transition-colors"
              >
                Not now
              </button>
            </div>
          </div>
          <button
            onClick={dismiss}
            className="flex-shrink-0 p-1 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>
      </div>
    </div>
  );
}
